import { Button, DestructiveConfirmDialog } from "@tellescope/ui";
import { CheckCheck, Trash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTellescopeMutation, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useCallback, useMemo } from "react";
import { CloseWithReasonsAction } from "./CloseWithReasonsAction";
import { PermissionWrapper } from "../../PermissionWrapper";
import { canCloseTicket } from "../utils";
import { formatDateTime } from "@tellescope/utils";

type TicketRecord = TellescopeModelFromName<"tickets">;

interface ActionsCellProps {
  ticket: TicketRecord;
}

/**
 * Action buttons for ticket operations
 * Mark Complete, Edit, Delete
 */
export function ActionsCell({ ticket }: ActionsCellProps) {
  const { t } = useTranslation("tickets");

  const deleteTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "deleteOne",
  });

  const closeTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "close_ticket",
  });

  const onMarkComplete = useCallback(
    (reason?: string) => {
      closeTicketMutation.mutateAsync({
        ticketId: ticket.id,
        closedForReason: reason,
      });
    },
    [closeTicketMutation, ticket.id]
  );

  const canClose = useMemo(() => canCloseTicket(ticket), [ticket]);

  return (
    <>
      <div className="flex items-center gap-1 justify-end">
        {ticket.closeReasons?.length && canClose ? (
          <CloseWithReasonsAction
            disabled={!!ticket.closedAt}
            reasons={ticket.closeReasons}
            onSelectReason={(reason) => onMarkComplete(reason)}
          />
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              onMarkComplete();
            }}
            disabled={!!ticket.closedAt || !canClose}
            hoverLabel={
              ticket.closedAt
                ? t("Completed: {{date}}", {
                    date: formatDateTime(ticket.closedAt, "MMM D, YYYY at hh:mm a", {
                      unit: "days",
                      amount: 7,
                      direction: "both",
                    }),
                  })
                : canClose
                ? t("Mark Complete")
                : t("Complete Ticket Actions before Closing")
            }
            data-trackingid="tickets-table-mark-complete"
          >
            <CheckCheck className="h-4 w-4" />
          </Button>
        )}
        <PermissionWrapper model="tickets" action="delete">
          <DestructiveConfirmDialog
            onConfirm={() => {
              deleteTicketMutation.mutateAsync(ticket.id);
            }}
            description={t("This will permanently delete the ticket. This action cannot be undone.")}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:text-red-600 hover:bg-red-100 transition-colors"
              hoverLabel={t("Delete Ticket")}
              data-trackingid="tickets-table-delete-ticket-dialog-open"
            >
              <Trash className="h-4 w-4" />
            </Button>
          </DestructiveConfirmDialog>
        </PermissionWrapper>
      </div>
    </>
  );
}
